import { createClient } from "$apis/parkito2/client/client.gen.ts";
import * as originalApi from "$apis/parkito2/index.ts";
import { session } from "./session-parkito.ts";
import { targetUrl } from "./api-parkito.ts";
import { derived, get, type Readable, writable } from "svelte/store";

function getErrorMessage(err: unknown): string {
  if (
    typeof err === "object" && err !== null
  ) {
    if ("message" in err && typeof err.message === "string") {
      return err.message;
    } else if ("description" in err && typeof err.description === "string") {
      return err.description;
    }
  } else if (typeof err === "string") {
    return err;
  }

  return "Unkonwn error";
}

function getErrorType(err: unknown): ErrorType | undefined {
  if (typeof err === "object" && err !== null) {
    if ("type" in err && typeof err.type === "string") {
      return err.type;
    }
  }
}

const client = createClient({ throwOnError: false });

targetUrl.subscribe((baseUrl: string) => {
  if (import.meta.env.DEV) console.log(`Setting baseUrl: "${baseUrl}"`);
  client.setConfig({
    ...client.getConfig(),
    baseUrl: baseUrl,
  });
});

session.subscribe((session) => {
  client.setConfig({
    ...client.getConfig(),
    headers: {
      "X-Authorization": "Bearer " + session?.jwt,
    },
  });
});

type Api = typeof originalApi;

type ErrorType = "network" | "mapped" | "stripe" | string;
export type ApiResponse<T> = {
  data: T;
  error: undefined;
  request: Request | undefined;
  response: Response | undefined;
} | {
  data: undefined;
  error: { message: string; type: ErrorType };
  request: Request | undefined;
  response: Response | undefined;
};

export function mock<T>(val: T): Promise<ApiResponse<T>> {
  return Promise.resolve({
    data: val,
    error: undefined,
    request: undefined,
    response: undefined,
  });
}

type WrappedApi =
  & {
    [K in keyof Api]: Api[K] extends (args: infer T) =>
      | Promise<
        & ({ data: infer D1; error: undefined } | {
          data: undefined;
          error: unknown;
        })
        & { request: Request; response: Response }
      >
      | Promise<{ data: infer _D2; request: Request; response: Response }> // for some reason D2 is almost always undefined? :S
      ? (args: T) => Promise<ApiResponse<D1>>
      : never;
  }
  & { mock: typeof mock };

const api = { mock: mock } as unknown as WrappedApi;

for (const [key, value] of Object.entries(originalApi)) {
  if (typeof value === "function") {
    // deno-lint-ignore no-explicit-any
    (api as any)[key] = async (args: any) => {
      try {
        // deno-lint-ignore no-explicit-any
        let resp = await (value as any)({
          client,
          ...args,
        });
        if (resp.error) {
          return {
            error: {
              type: "network",
              message: getErrorMessage(resp.error),
            },
          };
        }
        return resp;
      } catch (err: unknown) {
        return {
          error: {
            type: "network",
            message: getErrorMessage(err),
          },
        };
      }
    };
  }
}

/**
 * State management
 */
export type ApiCall<T> = {
  status: "loading";
  data: undefined;
  promise: Promise<ApiResponse<T>>;
  error: undefined;
  startedAt: number;
} | {
  status: "ok";
  data: T;
  error: undefined;
  promise: undefined;
  requestDuration: number;
} | {
  status: "ok"; // ok + data + promise = "discrete" reload
  data: T;
  error: undefined;
  promise: Promise<ApiResponse<T>>;
  requestDuration: number;
  startedAt: number;
} | {
  status: "idle";
  data: undefined;
  error: undefined;
  promise: undefined;
} | {
  status: "error";
  data: undefined;
  promise: undefined;
  error: { message: string; type: ErrorType };
};

export function Idle<T>(): ApiCall<T> {
  return {
    status: "idle",
    promise: undefined,
    data: undefined,
    error: undefined,
  };
}
export function Loading<T>(
  promise: Promise<ApiResponse<T>>,
  requestStartedAt: number,
): ApiCall<T> {
  return {
    status: "loading",
    promise,
    data: undefined,
    error: undefined,
    startedAt: requestStartedAt,
  };
}
export function Reloading<T>(
  promise: Promise<ApiResponse<T>>,
  previousData: T,
  previousRequestDuration: number,
  requestStartedAt: number,
): ApiCall<T> {
  return {
    status: "ok",
    promise,
    data: previousData,
    error: undefined,
    requestDuration: previousRequestDuration,
    startedAt: requestStartedAt,
  };
}
export function asError<T>(err: unknown, type: ErrorType): ApiResponse<T> {
  return {
    data: undefined,
    error: { message: getErrorMessage(err), type },
    request: undefined,
    response: undefined,
  };
}
export function Err<T>(err: { message: string; type: ErrorType }): ApiCall<T> {
  return { status: "error", error: err, data: undefined, promise: undefined };
}
export function Ok<T>(data: T, requestDuration: number): ApiCall<T> {
  return {
    status: "ok",
    error: undefined,
    data,
    promise: undefined,
    requestDuration,
  };
}
export function mapResp<T, U>(
  resp: ApiResponse<T>,
  fn: (r: T) => U,
): ApiResponse<U> {
  if (resp.data) {
    return {
      data: fn(resp.data),
      error: undefined,
      request: resp.request,
      response: resp.response,
    };
  } else {
    return resp as ApiResponse<U>;
  }
}

export function intoModel<T, M>(
  modelInit: new (arg: T) => M,
): (c: ApiResponse<T>) => ApiResponse<M> {
  return (r) => mapResp(r, (t: T) => new modelInit(t));
}

export function mapInto<T, M>(
  mapFn: (arg: T) => M,
): (c: ApiResponse<T>) => ApiResponse<M> {
  return (r) => mapResp(r, (t: T) => mapFn(t));
}

export type MappedCallState<T> = Readable<{
  call: ApiCall<T>;
  isLoading: () => boolean;
  isErr: () => boolean;
  isOk: () => boolean;
  onOk: (fn: (data: T) => void) => void;
}>;

// deno-lint-ignore no-explicit-any
export type ApiCallState<T, A extends any[] = []> = Readable<{
  call: ApiCall<T>;
  load: (...args: A) => Promise<ApiResponse<T>>;
  reload: (...args: A) => Promise<ApiResponse<T>>;
  reset: () => void;
  isLoading: () => boolean;
  isErr: () => boolean;
  isOk: () => boolean;
  onOk: (fn: (data: T) => void) => void;
  onErr: (fn: (err: unknown) => void) => void;
  // deno-lint-ignore no-explicit-any
  dependsOn: <D extends ApiCallState<any, any>[]>(
    deps: D,
    fn: (...calls: SuccessfulApiResponses<D>) => A,
  ) => void;
  map: <U>(fn: (t: T) => U) => MappedCallState<U>;
}>;

type SuccessfulApiResponses<T> = {
  // deno-lint-ignore no-explicit-any
  [K in keyof T]: T[K] extends ApiCallState<infer U, any> ? U : never;
};

export async function asApi<T>(
  promise: Promise<T>,
): Promise<ApiResponse<T>> {
  try {
    const data = await promise;
    return {
      data,
      error: undefined,
      request: undefined,
      response: undefined,
    };
  } catch (err: unknown) {
    return {
      data: undefined,
      error: { message: getErrorMessage(err), type: "mapped" },
      request: undefined,
      response: undefined,
    };
  }
}

// deno-lint-ignore no-explicit-any
export function fromApi<T, A extends any[]>(
  fn: (...args: A) => Promise<ApiResponse<T>>,
): ApiCallState<T, A> {
  const apiCall = writable<ApiCall<T>>(Idle());

  const load = (...args: A): Promise<ApiResponse<T>> => {
    const call = get(apiCall);
    if (call.status === "loading") {
      return call.promise;
    } else {
      const newPromise = fn(...args);
      const requestStartedAt = performance.now();

      newPromise.then((resp) => {
        const requestEndedAt = performance.now();
        const requestDuration = requestEndedAt - requestStartedAt;
        if (resp.data !== undefined) {
          apiCall.set(Ok(resp.data, requestDuration));
        } else if (resp.error) {
          apiCall.set(Err(resp.error));
        }
      }).catch((err) => apiCall.set(Err(err)));

      apiCall.set(Loading(newPromise, requestStartedAt));
      return newPromise;
    }
  };

  const reload = (...args: A): Promise<ApiResponse<T>> => {
    const call = get(apiCall);
    if (call.status === "loading") {
      return call.promise;
    } else if (call.status !== "ok") {
      return load(...args);
    } else {
      const newPromise = fn(...args);
      const requestStartedAt = performance.now();

      newPromise.then((resp) => {
        const requestEndedAt = performance.now();
        const requestDuration = requestEndedAt - requestStartedAt;
        if (resp.data !== undefined) {
          apiCall.set(Ok(resp.data, requestDuration));
        } else if (resp.error) {
          apiCall.set(Err(resp.error));
        }
      }).catch((err) => apiCall.set(Err(err)));

      apiCall.set(
        Reloading(
          newPromise,
          call.data,
          call.requestDuration,
          requestStartedAt,
        ),
      );
      return newPromise;
    }
  };

  const reset = () => {
    apiCall.set(Idle());
  };

  const onOk = (fn: (data: T) => void) => {
    apiCall.subscribe((call) => {
      if (call.status === "ok") {
        fn(call.data);
      }
    });
  };

  const onErr = (fn: (err: unknown) => void) => {
    apiCall.subscribe((call) => {
      if (call.status === "error") {
        fn(call.error);
      }
    });
  };

  // deno-lint-ignore no-explicit-any
  function dependsOn<D extends ApiCallState<any, any>[]>(
    deps: D,
    fn: (...calls: SuccessfulApiResponses<D>) => A,
  ) {
    const loadArgs = derived(deps, (deps): A | null => {
      for (const res of deps) {
        if (res.call.status !== "ok") {
          return null;
        }
      }
      // here we know all deps have loaded
      return fn(
        ...deps.map((res) => res.call.data) as SuccessfulApiResponses<D>,
      );
    });

    loadArgs.subscribe((args) => {
      if (args !== null) {
        return load(...args);
      }
    });
  }

  function map<U>(fn: (t: T) => U): MappedCallState<U> {
    const mappedCallState = derived(apiCall, (call) => {
      let mappedCall = Idle<U>();

      try {
        if (call.status === "ok") {
          mappedCall = Ok(fn(call.data), call.requestDuration);
        } else {
          mappedCall = call as ApiCall<U>;
        }
      } catch (err: unknown) {
        mappedCall = Err({
          message: getErrorMessage(err),
          type: getErrorType(err) ?? "mapped",
        });
      }

      const onOk = (fn: (data: U) => void) => {
        mappedCallState.subscribe((call) => {
          if (call.call.status === "ok") {
            fn(call.call.data);
          }
        });
      };

      return {
        call: mappedCall,
        onOk: onOk,
        isLoading: () => mappedCall.status === "loading",
        isErr: () => mappedCall.status === "error",
        isOk: () => mappedCall.status === "ok",
      };
    });

    return mappedCallState;
  }

  return derived(apiCall, (call) => ({
    call,
    load,
    reset,
    reload,
    isLoading: () => call.status === "loading",
    isOk: () => call.status === "ok",
    isErr: () => call.status === "error",
    onOk,
    onErr,
    dependsOn,
    map,
  }));
}

export default api;
